/**
 * The player.
 *
 * You start every game as a human — Kate was explicit ("you start off as a human and
 * you walk around in like a forest"). Morphing into an animal is bought with coupons
 * and lands in M5, so for now the human is all there is.
 *
 * Position is stored twice — `previous` and `position` — so the renderer can interpolate
 * between simulation steps. Only the fixed-step update writes them.
 */
import * as THREE from 'three'
import { clampToBorder, isInWater, PLAYER_RADIUS } from '../world/map'
import { heightAt, slopeAt } from '../world/terrain'
import type { TreeGrid } from '../world/trees'
import { Survival } from './survival'
import { BurrowField, DIG_SECONDS, type Burrow } from '../animals/burrow'
import { has, SPECIES, type Species, type SpeciesId } from '../animals/species'
import { Camouflage, climbableTree, flightCeiling } from '../animals/abilities'
import { canFight, FREEZE_HIDDEN, FREEZE_TIME, POUNCE_SPEED, verbFor, type Verb } from '../animals/verbs'
import { animate, buildAnimal, gaitFor, type AnimalModel } from '../animals/models'

/** Crouching is slower. Kate's wolf crouches to sniff, so it's a deliberate, slow act. */
const CROUCH_SPEED = 1.9
/** How far the capsule drops when crouching. */
const CROUCH_DROP = 0.35
/** Sprint multiplier over the species' walking speed. */
const SPRINT_MULTIPLIER = 1.6
/**
 * How quickly the body reaches full speed, and how quickly it stops. Instant start and
 * instant stop is the single biggest reason movement felt weightless — a bear should take
 * a moment to get going and a moment to stop, and a bunny should not.
 */
const ACCELERATION = 9
const BRAKING = 12
/** How far the body leans into a turn, in radians at full turn rate. */
const LEAN_MAX = 0.22
const GRAVITY = 18.0
/** Metres per second a bird gains altitude, capped at the canopy. */
const FLY_SPEED = 5.0
/** Metres per second a cat climbs a trunk. */
const CLIMB_SPEED = 3.5
/**
 * How far the body's origin sits above the ground. The terrain has height now, so this
 * is an offset from the land rather than an absolute Y.
 */
/** Fallback eye height, and the height the world's other distances were tuned against. */
const EYE_BASE = 0.9
/** How dark the contact shadow is in full daylight. */
const SHADOW_OPACITY = 0.28
/** How far up its own body an animal's eye sits. */
const EYE_FRACTION = 0.82
/** Keeps a fish from seeing through the ground and a whale from seeing over the trees. */
const MIN_EYE = 0.3
const MAX_EYE = 1.9
/** Climbing a steep slope costs speed, so terrain is a real consideration. */
const SLOPE_PENALTY = 0.55

/** "If you run into a tree, you bump into it and you lose one, one and a half hearts." */
/** Kept for the record: trees used to cost 1.5 hearts a bump until Kate ruled it out. */

export class Player {
  readonly object = new THREE.Group()
  readonly position = new THREE.Vector3(0, EYE_BASE, 0)
  readonly previous = new THREE.Vector3(0, EYE_BASE, 0)
  /** Heading in radians. Look-to-steer (M2) will drive this; for now it turns with strafe. */
  yaw = 0
  readonly survival = new Survival()
  /** True while sprinting — drains hunger twice as fast. */
  sprinting = false

  /** You start every game as a human — Kate was explicit about that. */
  species: Species = SPECIES.human
  /** Stable id, so burrow ownership survives a morph. */
  readonly id = 'local'
  /** The burrow currently sheltered in, if any. */
  shelteredIn: Burrow | null = null
  /** Seconds of held dig so far. */
  digProgress = 0
  /** Chameleon blending. */
  readonly camouflage = new Camouflage()
  /** True while airborne under the bird's own power. */
  flying = false
  /** True when both feet are off the ground. */
  get airborne(): boolean {
    return !this.grounded
  }

  /** How fast the body is actually travelling, which now lags the input. */
  get speedOverGround(): number {
    return Math.hypot(this.velX, this.velZ)
  }

  /** How far the body is banking, so the camera can roll with it. */
  get leanAmount(): number {
    return this.lean
  }
  /** True while climbing a trunk. */
  climbing = false
  private movingThisStep = false

  /** The one verb this animal has, or null. */
  get verb(): Verb | null {
    return verbFor(this.species.id)
  }
  /** Can this animal fight at all? Prey cannot — fleeing is their whole defence. */
  get canFight(): boolean {
    return canFight(this.species.id)
  }
  /** 0..1 — how completely hidden a freezing animal currently is. */
  frozen = 0
  /** Seconds left of a pounce. */
  pounceLeft = 0
  private pounceDirX = 0
  private pounceDirZ = 0
  /** Set on the frame a trample fires, so the world can react. */
  trampledThisStep = false
  /** How high this animal's eye sits above its feet. Set from the model on every morph. */
  eyeHeight = EYE_BASE
  /**
   * How strongly the contact shadow reads, 0..1. Driven from the daylight: with no sun
   * there is nothing to cast a shadow, so a hard disc at midnight looks painted on.
   */
  shadowStrength = 1
  /** True while actually feeding, so the head lowers to the ground. */
  grazing = false
  /** True while mid-bite, so the body leans in and the head thrusts. */
  biting = false
  /** Decays after taking damage, driving the flinch. */
  flinch = 0
  /** The last simulation step, so the pose blend is frame-rate independent. */
  private lastDt = 1 / 60
  /** True while standing in the pond — drinking and washing off hang off this in M3. */
  inWater = false
  /** True while crouched. The wolf's sniff (M4) keys off this. */
  crouching = false
  /** Counts bumps, so the effect can be tested and later hooked to a sound. */
  treeBumps = 0

  private verticalVelocity = 0
  private grounded = true
  /** Current horizontal velocity, so speed ramps instead of snapping. */
  private velX = 0
  private velZ = 0
  /** Smoothed lean and yaw rate, for leaning into turns. */
  private lean = 0
  private previousYaw = 0
  /** Advances always, so an idle animal still breathes. */
  private idlePhase = 0
  /**
   * The tree currently being pressed against. Damage fires on first contact only —
   * Kate described a discrete bump, not a drain, so holding forward into a trunk must
   * not empty the heart bar.
   */
  private contactTree: object | null = null

  private model: AnimalModel
  /** Advances with distance travelled, so legs cycle only while actually moving. */
  private stridePhase = 0

  constructor() {
    this.model = buildAnimal('human')
    this.object.add(this.model.group)
    this.object.position.copy(this.position)
  }

  /** Advance one fixed simulation step. */
  update(
    dt: number,
    forward: number,
    strafe: number,
    jump: boolean,
    crouch: boolean,
    trees?: TreeGrid,
    sprint = false,
    useVerb = false,
  ): void {
    this.previous.copy(this.position)
    this.trampledThisStep = false
    this.lastDt = dt
    // A flinch snaps on when hit and eases off over about a third of a second.
    this.flinch = Math.max(0, this.flinch - dt * 3)

    // FREEZE — prey cannot fight, so holding still is their defence. It only works if
    // you genuinely stop; the moment you move it breaks.
    if (this.verb === 'freeze' && useVerb && forward === 0 && strafe === 0) {
      this.frozen = Math.min(FREEZE_HIDDEN, this.frozen + dt / FREEZE_TIME)
    } else {
      this.frozen = Math.max(0, this.frozen - dt * 3)
    }

    // POUNCE — a cat closes the last gap in one leap rather than a chase.
    if (this.verb === 'pounce' && useVerb && this.pounceLeft <= 0 && this.grounded) {
      this.pounceLeft = 0.42
      this.pounceDirX = -Math.sin(this.yaw)
      this.pounceDirZ = -Math.cos(this.yaw)
      this.verticalVelocity = 4.2
      this.grounded = false
    }

    // TRAMPLE — a bear goes straight through what stops everything else.
    if (this.verb === 'trample' && useVerb) this.trampledThisStep = true

    this.crouching = crouch && this.grounded
    // You cannot sprint while crouched, and only forwards.
    this.sprinting = sprint && !this.crouching && forward > 0
    const base = this.species.speed
    const speed = this.crouching
      ? Math.min(CROUCH_SPEED, base)
      : this.sprinting
        ? base * SPRINT_MULTIPLIER
        : base

    if (this.pounceLeft > 0) {
      // The leap overrides normal movement for its duration.
      this.pounceLeft -= dt
      this.position.x += this.pounceDirX * POUNCE_SPEED * dt
      this.position.z += this.pounceDirZ * POUNCE_SPEED * dt
    } else {
      // Work out where the player wants to go, then ease the actual velocity toward it.
      // Snapping straight to full speed is what made every animal feel weightless.
      let wantX = 0
      let wantZ = 0
      if (forward !== 0 || strafe !== 0) {
        const length = Math.hypot(forward, strafe)
        const nf = forward / length
        const ns = strafe / length
        const sin = Math.sin(this.yaw)
        const cos = Math.cos(this.yaw)
        // Steep ground slows you down — a ridge is a decision, not just scenery.
        const terrainSpeed = speed * (1 - slopeAt(this.position.x, this.position.z) * SLOPE_PENALTY)
        // Forward is -Z in the player's local frame, matching the camera rig.
        wantX = (-nf * sin + ns * cos) * terrainSpeed
        wantZ = (-nf * cos - ns * sin) * terrainSpeed
      }
      // Heavier animals take longer to start and stop, which is most of what makes a
      // bear feel like a bear and a bunny feel like a bunny.
      const weight = 1 / Math.max(0.4, this.species.scale)
      const rate = (wantX === 0 && wantZ === 0 ? BRAKING : ACCELERATION) * weight
      const blend = 1 - Math.exp(-rate * dt)
      this.velX += (wantX - this.velX) * blend
      this.velZ += (wantZ - this.velZ) * blend
      this.position.x += this.velX * dt
      this.position.z += this.velZ * dt
    }

    if (trees) this.resolveTreeCollision(trees)
    clampToBorder(this.position)

    // Kate: you cannot jump out of a crouch without standing up first.
    // Birds climb by holding jump, but only to the canopy. Cats climb trunks. Everyone
    // else just jumps. Kate capped bird flight at tree height so the cat stays a counter.
    const canFly = has(this.species, 'fly')
    const climbTree = has(this.species, 'climb') && trees
      ? climbableTree(this.position.x, this.position.z, trees)
      : null

    this.flying = false
    this.climbing = false

    if (canFly && jump) {
      const ground = heightAt(this.position.x, this.position.z)
      const ceiling = ground + (trees ? flightCeiling(this.position.x, this.position.z, trees) : 9)
      this.flying = true
      this.grounded = false
      this.verticalVelocity = 0
      this.position.y = Math.min(ceiling, this.position.y + FLY_SPEED * dt)
    } else if (climbTree && jump) {
      this.climbing = true
      this.grounded = false
      this.verticalVelocity = 0
      this.position.y = Math.min(
        heightAt(this.position.x, this.position.z) + climbTree.canopyHeight,
        this.position.y + CLIMB_SPEED * dt,
      )
    } else {
      if (jump && this.grounded && !this.crouching) {
        this.verticalVelocity = this.species.jump
        this.grounded = false
      }
      if (!this.grounded) {
        this.verticalVelocity -= GRAVITY * dt
        this.position.y += this.verticalVelocity * dt
        const floor = heightAt(this.position.x, this.position.z) + this.eyeHeight
        if (this.position.y <= floor) {
          this.position.y = floor
          this.verticalVelocity = 0
          this.grounded = true
        }
      } else {
        // Walk up and down the land rather than through it.
        this.position.y = heightAt(this.position.x, this.position.z) + this.eyeHeight
      }
    }

    this.inWater = isInWater(this.position.x, this.position.z)
    this.survival.update(dt, this.sprinting, this.inWater)
    this.movingThisStep = Math.hypot(this.velX, this.velZ) > 0.35
    // Lean into a turn, easing back when the turn stops.
    let yawRate = this.yaw - this.previousYaw
    while (yawRate > Math.PI) yawRate -= Math.PI * 2
    while (yawRate < -Math.PI) yawRate += Math.PI * 2
    this.previousYaw = this.yaw
    const wantLean = Math.max(-1, Math.min(1, (yawRate / dt) / 2.5)) * LEAN_MAX
                     * Math.min(1, Math.hypot(this.velX, this.velZ) / 3)
    this.lean += (wantLean - this.lean) * Math.min(1, dt * 6)
    this.idlePhase += dt
    // Stride advances with distance, so the gait matches the speed and stops when you do.
    if (this.movingThisStep) this.stridePhase += speed * dt * 2.4
    this.camouflage.update(dt, this.species, this.movingThisStep)
  }

  /**
   * Push the player out of any trunk they've entered, and charge 1.5 hearts the moment
   * contact begins. Blocking is absolute — you cannot walk through a tree.
   */
  private resolveTreeCollision(trees: TreeGrid): void {
    let touching: object | null = null

    for (const tree of trees.near(this.position.x, this.position.z)) {
      const dx = this.position.x - tree.x
      const dz = this.position.z - tree.z
      const minimum = tree.radius + PLAYER_RADIUS
      const distance = Math.hypot(dx, dz)
      if (distance >= minimum) continue

      touching = tree
      // Degenerate case: dead centre. Push along +X rather than dividing by zero.
      if (distance < 1e-6) {
        this.position.x = tree.x + minimum
      } else {
        const scale = minimum / distance
        this.position.x = tree.x + dx * scale
        this.position.z = tree.z + dz * scale
      }
    }

    if (touching && touching !== this.contactTree) {
      // Kate, 2026-08-24: "when you run into trees, you take damage. I don't want you
      // to run into trees and take damage." You still stop dead against the trunk ---
      // that is the collision, and she has never objected to it --- but it no longer
      // costs hearts.
      this.treeBumps++
    }
    this.contactTree = touching
  }

  /**
   * Morph into another species. Kate's rule is that you buy the morph tool with
   * coupons; charging for it is the economy's job (M5), so this is the mechanism only.
   */
  morph(id: SpeciesId): void {
    const wasEye = this.eyeHeight
    this.species = SPECIES[id]
    this.refreshBody()
    // Keep the feet where they were rather than the eye: morphing from a bear into a
    // chameleon should lower your view to the ground, not drop the whole animal
    // through it.
    this.position.y += this.eyeHeight - wasEye
    this.previous.y += this.eyeHeight - wasEye
  }

  /** Every animal can dig a burrow. Hold dig for DIG_SECONDS to finish one. */
  updateDigging(dt: number, digging: boolean, field: BurrowField): Burrow | null {
    if (!digging || !this.grounded) {
      this.digProgress = 0
      return null
    }
    // Don't dig a second burrow on top of one that already exists here.
    if (field.at(this.position.x, this.position.z)) {
      this.digProgress = 0
      return null
    }
    this.digProgress += dt
    if (this.digProgress < DIG_SECONDS) return null
    this.digProgress = 0
    return field.dig(this.position.x, this.position.z, this.id, this.species.id)
  }

  /**
   * Try to shelter in whatever burrow is underfoot. Returns false when it belongs to a
   * different animal — Kate: "you cannot go into the other player's [burrow]. You can
   * if you're the same animal though."
   */
  tryShelter(field: BurrowField): boolean {
    const burrow = field.at(this.position.x, this.position.z)
    if (!burrow || !field.canEnter(burrow, this.id, this.species.id)) {
      this.shelteredIn = null
      return false
    }
    this.shelteredIn = burrow
    return true
  }

  get canBurrow(): boolean {
    return has(this.species, 'burrow')
  }

  /** Swap in the body for the current species. */
  private refreshBody(): void {
    this.object.remove(this.model.group)
    this.model.group.traverse((node) => {
      if (node instanceof THREE.Mesh) {
        node.geometry.dispose()
        ;(node.material as THREE.Material).dispose()
      }
    })
    this.model = buildAnimal(this.species.id)
    this.object.add(this.model.group)
    this.addContactShadow()

    // Every species used to see from exactly 0.9m, so a chameleon and a bear had the
    // same view of the world --- which is the opposite of Kate's "animals must play
    // differently". Measure the body that was just built and put the eye near the top
    // of it, so being small feels small.
    const bounds = new THREE.Box3().setFromObject(this.model.group)
    const height = bounds.max.y - bounds.min.y
    this.eyeHeight = Math.min(MAX_EYE, Math.max(MIN_EYE, height * EYE_FRACTION))
  }

/**
   * A dark disc under your own animal, so you can always find yourself.
   *
   * Measured contrast between an animal and the ground it stands on: a camel on sand
   * is 1.07, a dog in the jungle 1.01 --- the same brightness as the floor, so the
   * animal vanishes. Kate's colours are worth keeping (a sand-coloured camel is
   * *correct*), so this grounds the animal rather than repainting it.
   *
   * Deliberately the dumbest possible version: a flat circle, one colour, no texture.
   * Two attempts at a back-face outline and two at a gradient-textured shadow all came
   * out as solid black rectangles; every one of those failures involved either extra
   * geometry or texture compositing. This has neither.
   */
  private addContactShadow(): void {
    const bounds = new THREE.Box3().setFromObject(this.model.group)
    // setFromObject measures in world space, and the disc is a child of the group --- so
    // for a species whose group carries a scale (the bird is 0.46) the radius would be
    // applied twice and the shadow came out half size. Undo the group's scale here.
    const groupScale = this.model.group.scale.x || 1
    const footprint = Math.max(bounds.max.x - bounds.min.x, bounds.max.z - bounds.min.z)
    const radius = Math.max(0.28, footprint * 0.38) / groupScale
    const disc = new THREE.Mesh(
      new THREE.CircleGeometry(radius, 20),
      new THREE.MeshBasicMaterial({
        color: 0x0d1409, transparent: true, opacity: SHADOW_OPACITY, depthWrite: false,
      }),
    )
    disc.rotation.x = -Math.PI / 2
    disc.position.y = 0.04
    disc.renderOrder = 1
    // The camouflage pass below rewrites opacity on every mesh under this group. It
    // was resetting this disc to fully solid on the frame after it was built --- the
    // cause of five straight attempts rendering as flat black shapes.
    disc.userData.ownOpacity = true
    this.model.group.add(disc)
  }

  /** Push the interpolated transform into the scene graph for rendering. */
  applyInterpolated(alpha: number): void {
    this.object.position.lerpVectors(this.previous, this.position, alpha)
    // The model's own feet sit at y=0, so drop the group to stand it on the ground.
    this.object.position.y -= this.eyeHeight - (this.crouching ? CROUCH_DROP : 0)
    this.object.rotation.y = this.yaw
    // Bank into turns, and breathe when standing still — a body that never moves at all
    // reads as a statue.
    this.object.rotation.z = this.lean
    const breath = this.movingThisStep ? 0 : Math.sin(this.idlePhase * 1.9) * 0.012
    this.object.scale.set(1 + breath, (this.crouching ? 0.72 : 1) - breath, 1 + breath)

    animate(this.model, this.stridePhase, this.movingThisStep, this.flying,
            gaitFor(this.species.id), {
              // Head goes down to graze, drops when crouching to sniff, thrusts when biting.
              headDown: this.grazing ? 1 : 0,
              crouch: this.crouching ? 1 : 0,
              attack: this.biting ? 1 : 0,
              hurt: this.flinch,
            }, this.lastDt)

    // Chameleons fade the whole body out when they hold still.
    const opacity = this.camouflage.opacity
    this.model.group.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return
      if (node.userData.ownOpacity) {
        // The shadow fades with the light, and with the animal: a camouflaged
        // chameleon that still casts a dark disc on the grass has given itself away.
        const material = node.material as THREE.MeshBasicMaterial
        material.opacity = SHADOW_OPACITY * opacity * this.shadowStrength
        node.visible = material.opacity > 0.02
        return
      }
      const material = node.material as THREE.MeshStandardMaterial
      material.transparent = opacity < 1
      material.opacity = opacity
    })
  }
}
